import cx from 'classnames';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import Modal from 'react-modal';
import { DndProvider, useDrag, useDragLayer, useDrop } from 'react-dnd';
import { HTML5Backend, getEmptyImage } from 'react-dnd-html5-backend';
import { processAssignmentRequestsService } from '../../../../../services/scheduling';
import './styles.scss';
import { isResponseOk } from '../../../../../utility/isResponseOk';
import { HALF_LABELS, canPlaceRequest, getShiftRequestGroups } from './shiftRequests';

const DRAG_TYPE = 'assignment_request_user';

const GripIcon = () => (
  <svg className="request-user-grip" width="8" height="14" viewBox="0 0 8 14" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <circle cx="2" cy="2" r="1.25" />
    <circle cx="6" cy="2" r="1.25" />
    <circle cx="2" cy="7" r="1.25" />
    <circle cx="6" cy="7" r="1.25" />
    <circle cx="2" cy="12" r="1.25" />
    <circle cx="6" cy="12" r="1.25" />
  </svg>
);

const PlusIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 1.5V10.5M1.5 6H10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const RequestUserChipContent = ({ request }) => (
  <>
    <GripIcon />
    <span className="request-user-name" title={request.user_full_name}>
      {request.user_full_name}
    </span>
    {HALF_LABELS[request.type] && <span className="request-user-half">{HALF_LABELS[request.type]}</span>}
  </>
);

// Draggable chip representing a user that requested the shift. It can also be
// clicked to select it (useful on touch devices where HTML5 drag is unavailable).
// While dragged it stays in the list as a placeholder, and RequestDragLayer
// draws the chip under the cursor in place of the browser's drag image.
const RequestUserChip = ({ request, role, isSelected, onSelect }) => {
  const ref = useRef(null);
  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: DRAG_TYPE,
      // The width keeps the dragged chip as wide as the one in the list.
      item: () => ({ request, role, width: ref.current?.offsetWidth }),
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [request, role],
  );

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  drag(ref);

  return (
    <div ref={ref} className={cx('request-user-chip', { selected: isSelected, dragging: isDragging })} onClick={() => onSelect(request)}>
      <RequestUserChipContent request={request} />
    </div>
  );
};

// The chip following the cursor during a drag. It is rendered into the modal
// overlay: the modal box itself is transformed, which would offset a fixed layer.
const RequestDragLayer = () => {
  const { item, itemType, isDragging, offset } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    itemType: monitor.getItemType(),
    isDragging: monitor.isDragging(),
    offset: monitor.getSourceClientOffset(),
  }));

  if (!isDragging || itemType !== DRAG_TYPE || !offset) {
    return null;
  }

  return (
    <div className="assignment-requests-drag-layer">
      <div className="request-user-chip is-dragged" style={{ width: item.width, transform: `translate(${offset.x}px, ${offset.y}px)` }}>
        <RequestUserChipContent request={item.request} />
      </div>
    </div>
  );
};

// A single posted shift slot the manager can drop (or click-assign) a user into.
// Its schedule row number and unit are shown to the right of the block, e.g. "2 1st Floor".
const ShiftDropBlock = ({ slot, role, placedRequest, canAccept, selectedRequest, onPlace, onRemove }) => {
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: DRAG_TYPE,
      canDrop: (item) => !placedRequest && item.role === role && canAccept(item.request),
      drop: (item) => onPlace(slot.key, item.request.id),
      collect: (monitor) => ({ isOver: monitor.isOver(), canDrop: monitor.canDrop() }),
    }),
    [slot, role, placedRequest, canAccept, onPlace],
  );

  const canPlaceSelected = !placedRequest && !!selectedRequest && canAccept(selectedRequest);

  return (
    <div className="shift-drop-row">
      <div
        ref={drop}
        className={cx('shift-drop-block', {
          filled: !!placedRequest,
          'drop-active': isOver && canDrop,
          'can-place': canPlaceSelected,
        })}
        onClick={() => canPlaceSelected && onPlace(slot.key, selectedRequest.id)}
      >
        {placedRequest ? (
          <>
            <span className="shift-drop-user" title={placedRequest.user_full_name}>
              {placedRequest.user_full_name}
            </span>
            <button
              type="button"
              className="shift-drop-remove"
              aria-label={`Remove ${placedRequest.user_full_name}`}
              onClick={(e) => {
                e.stopPropagation();
                onRemove(slot.key);
              }}
            >
              <i className="icon-icon-x"></i>
            </button>
          </>
        ) : (
          <span className="shift-drop-placeholder">
            <PlusIcon />
            <span className="shift-drop-placeholder-text">{isOver && canDrop ? 'Release to assign' : 'Drop here'}</span>
          </span>
        )}
      </div>
      <span className="shift-drop-unit">
        <span className="shift-drop-unit-name" title={`${slot.rowNumber} ${slot.unitName}`}>
          <span className="shift-drop-unit-row">{slot.rowNumber}</span> {slot.unitName}
        </span>
        {HALF_LABELS[slot.half] && <span className="shift-drop-half">{HALF_LABELS[slot.half]}</span>}
      </span>
    </div>
  );
};

// Every pickup request for one day and shift, with the nurses' and the CNAs'
// requests side by side, each next to the posted slots of their role. Saving
// books every placed user; anyone left unplaced keeps their pending request,
// listed here only while a slot of their role and half is still open.
// Opened from the shift's column header: data is { date, shiftOrder, shiftLabel }.
const AssignmentRequestsModal = forwardRef(({ closeModal, data, periodData, setPeriodData, roleLabels }, ref) => {
  const { shiftLabel, date, shiftOrder } = data;
  // { [slotKey]: requestId }
  const [placements, setPlacements] = useState({});
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [overlayNode, setOverlayNode] = useState(null);

  const groups = useMemo(() => getShiftRequestGroups(periodData, date, shiftOrder), [periodData, date, shiftOrder]);

  // Placements whose slot or request vanished after a period refresh are dropped.
  const placementList = useMemo(() => {
    const slotsByKey = new Map(groups.flatMap((group) => group.slots.map((slot) => [slot.key, slot])));
    const requestsById = new Map(groups.flatMap((group) => group.requests.map((request) => [request.id, request])));

    return Object.entries(placements)
      .map(([slotKey, requestId]) => ({ slot: slotsByKey.get(slotKey), request: requestsById.get(requestId) }))
      .filter((placement) => placement.slot && placement.request);
  }, [groups, placements]);

  const placedRequestBySlot = useMemo(
    () => Object.fromEntries(placementList.map((placement) => [placement.slot.key, placement.request])),
    [placementList],
  );
  const placedRequestIds = useMemo(() => new Set(placementList.map((placement) => placement.request.id)), [placementList]);

  // The chip picked for click-to-place, with the role of its group: { request, role } or null.
  const selected = useMemo(() => {
    if (placedRequestIds.has(selectedRequestId)) return null;

    for (const group of groups) {
      const request = group.requests.find((item) => item.id === selectedRequestId);
      if (request) return { request, role: group.role };
    }
    return null;
  }, [groups, selectedRequestId, placedRequestIds]);

  const totalSlots = groups.reduce((sum, group) => sum + group.slots.length, 0);

  // Place a request into a slot, removing it from any slot it already occupied.
  const handlePlace = useCallback((slotKey, requestId) => {
    setPlacements((prev) => ({
      ...Object.fromEntries(Object.entries(prev).filter(([, id]) => id !== requestId)),
      [slotKey]: requestId,
    }));
    setSelectedRequestId(null);
  }, []);

  const handleRemovePlacement = (slotKey) => {
    setPlacements((prev) => {
      const { [slotKey]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const handleSelectRequest = (request) => {
    setSelectedRequestId((prev) => (prev === request.id ? null : request.id));
  };

  const getRoleLabel = (role) => (role === 'nurse' ? roleLabels?.nurse_role_label : roleLabels?.cna_role_label);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Every placement goes in as a reassignment: the backend checks that the
      // target slot is still free and of the request's role.
      const payload = {
        approve_assignment_request_id: null,
        deny_assignment_request_ids: [],
        reassignments: placementList.map(({ request, slot }) => ({
          assignment_request: request.id,
          scheduling_cell: slot.cell.id,
          type: slot.half,
        })),
      };

      const response = await processAssignmentRequestsService(payload);

      if (isResponseOk(response)) {
        const updatedCells = Array.isArray(response.data) ? response.data : [response.data];
        const updatedById = new Map(updatedCells.map((c) => [c.id, c]));

        setPeriodData((prev) => ({
          ...prev,
          units: prev.units.map((unit) => ({
            ...unit,
            roles: unit.roles.map((role) => ({
              ...role,
              cells: role.cells.map((c) => (updatedById.has(c.id) ? updatedById.get(c.id) : c)),
            })),
          })),
        }));

        closeModal();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      className={cx('modal-alert assignment-requests-modal', { 'has-two-groups': groups.length > 1 })}
      isOpen
      onRequestClose={closeModal}
      contentLabel="Assignment Requests Modal"
      ariaHideApp={false}
      contentRef={(node) => {
        ref.current = node;
      }}
      overlayRef={setOverlayNode}
    >
      <h2 className="modal-header">
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="5" y="5" width="38" height="38" rx="19" fill="#F4EBFF" />
          <rect x="5" y="5" width="38" height="38" rx="19" stroke="#F9F5FF" strokeWidth="10" />
          <path
            d="M26.6667 17.3359V20.0026M21.3333 17.3359V20.0026M18 22.6693H30M19.3333 18.6693H28.6667C29.403 18.6693 30 19.2662 30 20.0026V29.3359C30 30.0723 29.403 30.6693 28.6667 30.6693H19.3333C18.597 30.6693 18 30.0723 18 29.3359V20.0026C18 19.2662 18.597 18.6693 19.3333 18.6693Z"
            stroke="#7F56D9"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span className="modal-header-title">Request for shift booking</span>
        <button className="btn btn-icon" onClick={closeModal}>
          <i className="icon-icon-x"></i>
        </button>
      </h2>

      <div className="shift-requests-meta">
        <span className="meta-shift">{shiftLabel} shift</span>
        <span className="meta-date">{dayjs(date).format('dddd, D MMMM YYYY')}</span>
      </div>

      <DndProvider backend={HTML5Backend}>
        {overlayNode && createPortal(<RequestDragLayer />, overlayNode)}
        <div className="shift-requests-groups">
          {groups.map((group) => (
            <section className="shift-requests-group" key={group.role}>
              <div className="shift-requests-group-body">
                <h4 className="shift-requests-column-title is-requests">{getRoleLabel(group.role)}</h4>
                <span className="shift-requests-column-title is-units">Unit</span>
                <div className="request-users-list">
                  {group.requests
                    .filter((request) => !placedRequestIds.has(request.id))
                    .map((request) => (
                      <RequestUserChip
                        key={request.id}
                        request={request}
                        role={group.role}
                        isSelected={selected?.request.id === request.id}
                        onSelect={handleSelectRequest}
                      />
                    ))}
                </div>

                <div className="shift-blocks-list">
                  {group.slots.map((slot) => (
                    <ShiftDropBlock
                      key={slot.key}
                      slot={slot}
                      role={group.role}
                      placedRequest={placedRequestBySlot[slot.key]}
                      canAccept={(request) => canPlaceRequest(request, slot, placementList)}
                      selectedRequest={selected?.role === group.role ? selected.request : null}
                      onPlace={handlePlace}
                      onRemove={handleRemovePlacement}
                    />
                  ))}
                </div>
              </div>
            </section>
          ))}
        </div>
      </DndProvider>

      <div className="modal-footer">
        <span className="positions-filled">
          {placementList.length} of {totalSlots} positions filled
        </span>
        <div className="modal-footer-actions">
          <button className="btn btn-outline" onClick={closeModal}>
            Cancel
          </button>
          <button
            className="btn"
            disabled={!placementList.length || isSaving}
            onClick={(e) => {
              e.stopPropagation();
              handleSave();
            }}
          >
            Save
          </button>
        </div>
      </div>
    </Modal>
  );
});

export default AssignmentRequestsModal;
